import React, { useEffect, useMemo, useState } from 'react';

import {
  Button,
  Divider,
  Input,
  Modal,
  RefreshIcon,
  Select,
  Text,
  TextButton,
  type TypeSelectOption,
  dialogService,
} from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';

import { Table, TableTopField } from '~/components/common';

import { useGetCategoriesCodesParentCodeQuery, useGetCategoriesTypesTypeQuery } from '~/hooks/product';
import { receiveKeys, useGetFilterAllQuery, useGetRequestGoodsQuery } from '~/hooks/receive';
import { useReceiveStore, useUserInfoStore } from '~/store';
import { addIf, getReceiveAddGoodsModalColumnDefs, transformToSelectOptions } from '~/utils';

import { OPTION_ALL } from '~/constants/common';

import type { TypeAddGoodsModalFilter, TypeAddGoodsModalGoods, TypeGetRequestGoodsParams } from '~/types/receive.types';

import styles from './ReceiveRequestAddGoodsModal.module.scss';

const DEFAULT_ADD_GOODS_FILTER = {
  mainCategoryCode: OPTION_ALL,
  middleCategoryCode: OPTION_ALL,
  thirdCategoryCode: OPTION_ALL,
  keywordCode: null,
  keyword: '',
};

type TypeReceiveRequestAddGoodsModalProps = {
  ownerCode: string;
  temperature: string;
  supplierCode: string;
  open: boolean;
  onCancel: () => void;
  onConfirm: (data: TypeAddGoodsModalGoods[]) => void;
};

/**
 * [입고관리 > 입고요청관리] 입고상품 추가 모달 컴포넌트
 */
const ReceiveRequestAddGoodsModal = ({
  ownerCode,
  temperature,
  supplierCode,
  open,
  onCancel,
  onConfirm,
}: TypeReceiveRequestAddGoodsModalProps) => {
  const queryClient = useQueryClient();

  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermissionNFA = hasPermission([{ ownerType: 'NFA' }]);

  const filterOptionsQuery = useGetFilterAllQuery('ADD_RECEIVING_GOODS');

  const requestModalGoodsList = useReceiveStore((state) => state.requestModalGoodsList);

  const registeredGoodsCodesSet = useMemo(
    () => new Set(requestModalGoodsList.map(({ goodsCode }) => goodsCode)),
    [requestModalGoodsList]
  );
  const registeredGoodsCodes = useMemo(() => Array.from(registeredGoodsCodesSet), [registeredGoodsCodesSet]);

  const { columnDefs, isRowSelectable } = useMemo(
    () => getReceiveAddGoodsModalColumnDefs({ hasPermissionNFA, registeredGoodsCodes }),
    [hasPermissionNFA, registeredGoodsCodes]
  );

  const [addGoodsFilter, setAddGoodsFilter] = useState<TypeAddGoodsModalFilter>(DEFAULT_ADD_GOODS_FILTER);

  const [selectedGoods, setSelectedGoods] = useState<TypeAddGoodsModalGoods[]>([]);

  const [listQueryParams, setListQueryParams] = useState<TypeGetRequestGoodsParams | null>(null);

  // 대분류 카테고리 조회
  const mainCategoryQuery = useGetCategoriesTypesTypeQuery({ type: 'MAIN', showGlobalDimOverlay: false });

  // 중분류 카테고리 조회 (대분류 선택 시)
  const selectedMainCode = addGoodsFilter.mainCategoryCode.value;
  const middleCategoryQuery = useGetCategoriesCodesParentCodeQuery({
    parentCode: selectedMainCode !== OPTION_ALL.value ? selectedMainCode : null,
    showGlobalDimOverlay: false,
  });

  // 소분류 카테고리 조회 (중분류 선택 시)
  const selectedMiddleCode = addGoodsFilter.middleCategoryCode.value;
  const thirdCategoryQuery = useGetCategoriesCodesParentCodeQuery({
    parentCode: selectedMiddleCode !== OPTION_ALL.value ? selectedMiddleCode : null,
    showGlobalDimOverlay: false,
  });

  // 카테고리 옵션 변환
  const optionMainCategory = useMemo(() => {
    if (!mainCategoryQuery.data) return [OPTION_ALL];
    return [OPTION_ALL, ...transformToSelectOptions(mainCategoryQuery.data, 'code', 'name')];
  }, [mainCategoryQuery.data]);

  const optionMiddleCategory = useMemo(() => {
    if (!middleCategoryQuery.data) return [OPTION_ALL];
    return [OPTION_ALL, ...transformToSelectOptions(middleCategoryQuery.data, 'code', 'name')];
  }, [middleCategoryQuery.data]);

  const optionThirdCategory = useMemo(() => {
    if (!thirdCategoryQuery.data) return [OPTION_ALL];
    return [OPTION_ALL, ...transformToSelectOptions(thirdCategoryQuery.data, 'code', 'name')];
  }, [thirdCategoryQuery.data]);

  const requestGoodsQuery = useGetRequestGoodsQuery({
    ownerCode,
    params: listQueryParams ?? ({} as TypeGetRequestGoodsParams),
    enabled: listQueryParams !== null,
  });

  useEffect(() => {
    if (filterOptionsQuery.data?.keywordType[0] && addGoodsFilter.keywordCode === null) {
      setAddGoodsFilter((prev) => ({ ...prev, keywordCode: filterOptionsQuery.data.keywordType[0] }));
    }
  }, [filterOptionsQuery.data]);

  const handleClickSearch = () => {
    const { mainCategoryCode, middleCategoryCode, thirdCategoryCode, keywordCode, keyword } = addGoodsFilter;

    const params = {
      warehouse: temperature,
      ownerSupplierCode: supplierCode,
      ...addIf(mainCategoryCode.value, 'mainCategoryCode'),
      ...addIf(middleCategoryCode.value, 'middleCategoryCode'),
      ...addIf(thirdCategoryCode.value, 'thirdCategoryCode'),
      ...addIf(keyword && keywordCode?.value, 'searchKeyword', keywordCode?.value),
      ...addIf(keyword, 'searchText'),
    };

    queryClient.invalidateQueries({
      queryKey: receiveKeys.requestGoods(ownerCode, params),
    });

    setListQueryParams(params);
  };

  const handleClickFilterReset = () => {
    setAddGoodsFilter({ ...DEFAULT_ADD_GOODS_FILTER, keywordCode: filterOptionsQuery.data?.keywordType[0] ?? null });
  };

  const handleChangeMainCategory = (_: unknown, option: TypeSelectOption<string>) => {
    setAddGoodsFilter({
      ...addGoodsFilter,
      mainCategoryCode: option,
      middleCategoryCode: OPTION_ALL,
      thirdCategoryCode: OPTION_ALL,
    });
  };

  const handleChangeMiddleCategory = (_: unknown, option: TypeSelectOption<string>) => {
    setAddGoodsFilter({
      ...addGoodsFilter,
      middleCategoryCode: option,
      thirdCategoryCode: OPTION_ALL,
    });
  };

  const handleChangeThirdCategory = (_: unknown, option: TypeSelectOption<string>) => {
    setAddGoodsFilter({ ...addGoodsFilter, thirdCategoryCode: option });
  };

  const handleChangeKeywordCode = (_: unknown, option: TypeSelectOption<string>) => {
    setAddGoodsFilter({ ...addGoodsFilter, keywordCode: option });
  };

  const handleChangeKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddGoodsFilter((addGoodsFilter) => ({ ...addGoodsFilter, keyword: e.target.value }));
  };

  const handleChangeRowSelection = (rows: TypeAddGoodsModalGoods[]) => {
    setSelectedGoods(rows);
  };

  const checkLimitExceeded = (): { exceeded: boolean; type: 'rowCount' | 'skuCountOrQuantity' | null } => {
    const MAX_ROW_COUNT = 200;
    const MAX_SKU_COUNT = 200;
    const MAX_QUANTITY = 20000;

    const registeredRowCount = requestModalGoodsList.length;
    const newRowCount = selectedGoods.length;

    // [1순위] 행 제한 체크
    if (registeredRowCount + newRowCount > MAX_ROW_COUNT) {
      return { exceeded: true, type: 'rowCount' };
    }

    // [2순위] 품목/수량 제한 체크
    const selectedGoodsCodesSet = new Set(selectedGoods.map(({ goodsCode }) => goodsCode));
    const registeredSkuCount = registeredGoodsCodesSet.size;
    const newSkuCount = Array.from(selectedGoodsCodesSet).filter((code) => !registeredGoodsCodesSet.has(code)).length;
    const registeredQuantity = requestModalGoodsList.reduce((sum, item) => sum + (item.totalQty || 0), 0);

    if (registeredSkuCount + newSkuCount > MAX_SKU_COUNT || registeredQuantity > MAX_QUANTITY) {
      return { exceeded: true, type: 'skuCountOrQuantity' };
    }

    return { exceeded: false, type: null };
  };

  const handleConfirm = () => {
    const limitResult = checkLimitExceeded();

    if (limitResult.exceeded) {
      const message =
        limitResult.type === 'rowCount'
          ? '최대 200행까지만 등록 가능합니다.\n확인 후 다시 시도해 주세요.'
          : '최대 200종의 품목, 20,000개의 수량까지만\n등록 가능합니다. 확인 후 다시 시도해 주세요.';

      dialogService.addDialog({ children: message });

      return;
    }

    onConfirm(selectedGoods);
  };

  return (
    <Modal
      title="입고 상품 추가"
      size="large"
      depth={2}
      confirmText="추가"
      open={open}
      disabled={selectedGoods.length === 0}
      onCancel={onCancel}
      onConfirm={handleConfirm}
    >
      <div className={styles['contents-container']}>
        <div className={styles['filter-section']}>
          <div className={styles['filter-container']}>
            <div className={styles['filter-wrapper']}>
              <Text className={styles['label']} size="small" weight="semi-bold">
                대분류
              </Text>
              <Select
                className={styles['select']}
                size="small"
                options={optionMainCategory}
                value={addGoodsFilter.mainCategoryCode}
                onChange={handleChangeMainCategory}
              />
            </div>
            <div className={styles['filter-wrapper']}>
              <Text className={styles['label']} size="small" weight="semi-bold">
                중분류
              </Text>
              <Select
                className={styles['select']}
                size="small"
                disabled={addGoodsFilter.mainCategoryCode.value === OPTION_ALL.value}
                options={optionMiddleCategory}
                value={addGoodsFilter.middleCategoryCode}
                onChange={handleChangeMiddleCategory}
              />
            </div>
            <div className={styles['filter-wrapper']}>
              <Text className={styles['label']} size="small" weight="semi-bold">
                소분류
              </Text>
              <Select
                className={styles['select']}
                size="small"
                disabled={addGoodsFilter.middleCategoryCode.value === OPTION_ALL.value}
                options={optionThirdCategory}
                value={addGoodsFilter.thirdCategoryCode}
                onChange={handleChangeThirdCategory}
              />
            </div>
            <div className={styles['keyword-filter-wrapper']}>
              <div className={styles['filter-wrapper']}>
                <Text className={styles['label']} size="small" weight="semi-bold">
                  키워드
                </Text>
                <Select
                  className={styles['select']}
                  size="small"
                  options={filterOptionsQuery.data?.keywordType ?? []}
                  value={addGoodsFilter.keywordCode}
                  onChange={handleChangeKeywordCode}
                />
              </div>
              <Input
                type="text"
                size="small"
                placeholder="입력"
                value={addGoodsFilter.keyword}
                onChange={handleChangeKeyword}
              />
            </div>
          </div>
          <div className={styles['filter-actions']}>
            <Button className={styles['search-button']} size="small" onClick={handleClickSearch}>
              검색
            </Button>
            <TextButton
              className={styles['reset-button']}
              size="small"
              color="secondary"
              leftIcon={<RefreshIcon size={16} />}
              onClick={handleClickFilterReset}
            >
              필터 초기화
            </TextButton>
          </div>
        </div>
        <Divider color="gray100" />
        <div className={styles['view-section']}>
          <TableTopField
            dataTotalCount={requestGoodsQuery.data?.goodsList?.length ?? 0}
            selectedCount={selectedGoods.length}
          />
          <Table
            className={styles['table']}
            data={requestGoodsQuery.data?.goodsList ?? null}
            columns={columnDefs}
            rowStatusType={!requestGoodsQuery.data ? 'INIT' : 'EMPTY'}
            selection={{
              isRowSelectable,
              showDisabledCheckboxes: true,
              dimUnselectableRows: true,
              onChange: handleChangeRowSelection,
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ReceiveRequestAddGoodsModal;
